import styled from 'styled-components';

import { Card } from '@components/card';
import { ChatBubble } from '@components/chat-bubble';
import { Modal, useModal } from '@components/modal';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';
import { abbreviateDate } from '@utils/date';

import { useCompanyDetails } from 'api/company/hooks';

export const usePreviewSmsModal = () => useModal(PreviewSms, { variant: 'REGULAR' });

export type PreviewSmsProps = {
  to: string | string[];
  textMessage: string;
  date?: Date;
};

export const PreviewSms = (props: PreviewSmsProps) => {
  const { to, textMessage, date } = props;
  const { t } = useScopedTranslate(locale);
  const companyQuery = useCompanyDetails();
  const message = textMessage.replaceAll('{{ ', '{{').replaceAll(' }}', '}}');

  const recipient = Array.isArray(to) ? (to.length > 0 ? to.join(', ') : t('noRecipient')) : to;
  const textDate = date ? abbreviateDate(date, 'en', 'weekday-datetime') : t('date');

  return (
    <Modal>
      <Modal.SimpleHeader title={t('title')} />
      <Modal.Content>
        <Stack space={6}>
          <Stack space={1}>
            <Stack space={4} orientation="horizontal">
              <StyledText text={t('from')} font="semibold14" color="textSecondary" />
              <Text text={companyQuery.data?.name} font="regular14" />
            </Stack>
            <Separator />
            <Stack space={4} orientation="horizontal">
              <StyledText text={t('to')} font="semibold14" color="textSecondary" />
              <Text text={recipient} font="regular14" />
            </Stack>
            <Separator />
          </Stack>
          <StyledCard space={2}>
            <Stack space={2}>
              <Stack align="center">
                <Text text={t('textmessage')} font="regular14" color="textSecondary" />
                <Text text={textDate} font="regular14" color="textSecondary" />
              </Stack>
              <Stack orientation="horizontal" align="flex-start">
                {message ? (
                  <ChatBubble color="green100" direction="left" text={message} />
                ) : (
                  <Text color="textHelper" text={t('noContent')} />
                )}
              </Stack>
            </Stack>
          </StyledCard>
        </Stack>
      </Modal.Content>
    </Modal>
  );
};

const StyledCard = styled(Card)({
  padding: pxToRem(24),
});

const StyledText = styled(Text)({
  width: pxToRem(80),
});

const locale = {
  en: {
    title: 'SMS preview',
    from: 'From',
    to: 'To',
    textmessage: 'Text message',
    date: 'Day, Date at Time',
    noRecipient: 'Sms has no recipients',
    noContent: 'Sms has no content',
  },
};
